import React, { useCallback, useState } from 'react';
import { Button, Card, Grid, Form, Radio, Icon } from 'semantic-ui-react';
import classNames from 'classnames';
import addToDate from 'date-fns/add';
import DateTimePicker from '../../../../components/DateTimePicker';
import {
  CryptoBuySellCorridor,
  getDefaultFeeCurrencyId,
} from '../../../../types/models/CryptoBuySellCorridor';
import CryptoBuySellRanges from './CryptoBuySellRanges';
import { SetStateCallback } from '../../../../types/utility';
import { CryptoBuySellRange } from '../../../../types/models/CryptoBuySellRange';
import CryptoBuySellCorridorConfigurationHeader from './CryptoBuySellCorridorConfigurationHeader';
import { ValidationErrors } from '../../../../types/models/ValidationErrors';
import { TransferDirection } from '../../../../types/enums/TransferDirection';
import { FeeValueType } from '../../../../types/enums/FeeValueType';
import { AmountType } from 'src/types/enums/AmountType';
import { TransactionType } from 'src/types/enums/TransactionType';

export type ChangedCryptoBuySellCorridor = Partial<
  Omit<CryptoBuySellCorridor, 'id' | 'configuration'>
>;

interface CryptoBuySellCorridorViewProps {
  corridor: CryptoBuySellCorridor;
  transferDirection: keyof typeof TransferDirection;
  onChange: (
    id: CryptoBuySellCorridor['id'],
    changed: Partial<CryptoBuySellCorridor>,
  ) => void;
  onEditConfiguration: (corridor: CryptoBuySellCorridor) => void;
  onCopy: (corridor: CryptoBuySellCorridor) => void;
  onDelete: (id: CryptoBuySellCorridor['id']) => void;
  readOnly: boolean;
  validationErrors?: ValidationErrors;
}

export default function CryptoBuySellCorridorView({
  corridor,
  onChange,
  onEditConfiguration,
  onCopy,
  onDelete,
  transferDirection,
  readOnly = false,
  validationErrors = {},
}: CryptoBuySellCorridorViewProps): JSX.Element {
  const setRanges = useCallback(
    (func: SetStateCallback<CryptoBuySellRange>) => {
      const ranges = func(corridor.ranges);
      const changed: ChangedCryptoBuySellCorridor = { ranges };
      onChange(corridor.id, changed);
    },
    [corridor, onChange],
  );
  const onClickCopy = useCallback(() => {
    onCopy(corridor);
  }, [corridor, onCopy]);
  const onClickDelete = useCallback(() => {
    onDelete(corridor.id);
  }, [corridor.id, onDelete]);
  const onClickEdit = useCallback(() => {
    onEditConfiguration(corridor);
  }, [corridor, onEditConfiguration]);

  const [expanded, setExpanded] = useState(true);
  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, [setExpanded]);

  const classNameExpanded = classNames({
    crypto_form_collect__corridor_expand: true,
    'crypto_form_collect__corridor_expand--expanded': expanded,
  });

  const [collectedAt, setCollectedAt] = useState(() => {
    if (!corridor.collectedAtLocal) {
      return '';
    }
    if (typeof corridor.collectedAtLocal !== 'object') {
      // display time as it is in the location
      return addToDate(new Date(corridor.collectedAtLocal), {
        minutes: new Date().getTimezoneOffset(),
      });
    }
    return corridor.collectedAtLocal;
  });

  const onChangeCollectedAt = useCallback(
    ({ date }) => {
      setCollectedAt(date || '');
      onChange(corridor.id, { collectedAtLocal: date });
    },
    [setCollectedAt, onChange, corridor.id],
  );

  const onChangeMode = useCallback(
    (e, { name, value }) => {
      const fieldName = name.split('_')[0];
      onChange(corridor.id, { [fieldName]: value });
    },
    [onChange, corridor.id],
  );

  return (
    <Card fluid>
      <div className="crypto_form_collect__corridor_header">
        <div className={classNameExpanded} onClick={toggleExpanded}>
          <Icon name="dropdown" />
        </div>
        <CryptoBuySellCorridorConfigurationHeader
          configuration={corridor}
          transferDirection={transferDirection}
        />
      </div>
      {expanded && (
        <>
          <Grid style={{ margin: 0 }}>
            <Grid.Column width={5}>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Form.Field label="I am entering" />
                    <Form.Field>
                      <Radio
                        label="Ranges"
                        data-qa="ranges"
                        name={`typeAmounts_buySell_${corridor.id}`}
                        value={AmountType.RANGE}
                        checked={corridor.typeAmounts === AmountType.RANGE}
                        onChange={onChangeMode}
                        readOnly={readOnly}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Radio
                        label="Single amount"
                        data-qa="single"
                        name={`typeAmounts_buySell_${corridor.id}`}
                        value={AmountType.SINGLE}
                        checked={corridor.typeAmounts === AmountType.SINGLE}
                        onChange={onChangeMode}
                        readOnly={readOnly}
                      />
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Form.Field style={{ marginTop: 34 }}>
                      <Radio
                        label="Rates"
                        data-qa="rates"
                        name={`typeRates_buySell_${corridor.id}`}
                        value={FeeValueType.FIXED}
                        checked={corridor.typeRates === FeeValueType.FIXED}
                        onChange={onChangeMode}
                        readOnly={readOnly}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Radio
                        data-qa="margins"
                        label="Margins (%)"
                        name={`typeRates_buySell_${corridor.id}`}
                        value={FeeValueType.PERCENTAGE}
                        checked={corridor.typeRates === FeeValueType.PERCENTAGE}
                        onChange={onChangeMode}
                        readOnly={readOnly}
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Grid.Column>
            <Grid.Column width={7}></Grid.Column>
            <Grid.Column width={4}>
              <Form.Field label="Collected at (location time)" />
              <DateTimePicker
                className="crypto_form_collect__input_field--width_popup_error"
                value={collectedAt}
                onChange={onChangeCollectedAt}
                readOnly={readOnly}
                error={validationErrors.collectedAtLocal}
                popperClassName="crypto_form_collect--zIndex3"
              />
            </Grid.Column>
          </Grid>
          <CryptoBuySellRanges
            defaultCurrencyId={getDefaultFeeCurrencyId(corridor)}
            typeAmounts={corridor.typeAmounts}
            typeRates={corridor.typeRates}
            feeTypes={
              corridor.transactionType === TransactionType.EXCHANGE_TRADE
                ? [FeeValueType.PERCENTAGE]
                : undefined
            }
            ranges={corridor.ranges}
            setRanges={setRanges}
            readOnly={readOnly}
            validationErrors={
              validationErrors.rangesErrors as ValidationErrors | undefined
            }
          />
          {!readOnly && (
            <div className="crypto_form_collect__corridor_footer">
              <Grid>
                <Grid.Row verticalAlign="bottom">
                  <Grid.Column floated="left" width="6">
                    <Button
                      data-qa="clone-table"
                      onClick={onClickCopy}
                      type="button"
                    >
                      Clone Table
                    </Button>
                  </Grid.Column>
                  <Grid.Column floated="right" width="6" textAlign="right">
                    <div style={{ marginBottom: '1em' }}>
                      <Button
                        data-qa="edit-table-settings"
                        onClick={onClickEdit}
                        type="button"
                      >
                        Edit Table Settings
                      </Button>
                    </div>
                    <div>
                      <Button
                        data-qa="delete-table"
                        onClick={onClickDelete}
                        type="button"
                      >
                        Delete Table
                      </Button>
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
